import { z } from 'zod';
import { HttpUrlSchema, UnixTimestampSchema } from '../common/schemas';
import { DBCourseCourseIdSchema } from '../courses/schemas';
import { DBStaffStaffIdSchema } from '../staff/schemas';
import {
  DBStudentFirstNameSchema,
  DBStudentLastNameSchema,
  DBStudentStudentIdSchema,
} from '../students/schemas';
import { TUTORIAL_LOCATIONS } from './constants';
import { TutorialFrequency } from './enums';

/**
 * Shared & Helper Schemas
 */

export const DBTutorialIdSuffixSchema = z.string().regex(/^[A-Za-z0-9]{5}$/);
export const DBTutorialBookingNoteSchema = z.string().max(2048);

export const TutorialTotalRepeatsSchema = z.number().int().min(1).max(50);
export const TutorialFrequencySchema = z.enum(TutorialFrequency);
export const TutorialBookingNoteSchema = DBTutorialBookingNoteSchema;

/**
 * DB Schemas
 */

export const DBTutorialCourseSchema = z.lazy(() => DBCourseCourseIdSchema);
export const DBTutorialStartDatetimeIdentifierSchema = z.string().refine(val => {
  const parts = val.split('#');

  return (
    parts.length === 2 &&
    UnixTimestampSchema.safeParse(Number(parts[0])).success &&
    DBTutorialIdSuffixSchema.safeParse(parts[1]).success
  );
});
export const DBTutorialAttendeesSchema = z.record(
  z.lazy(() => DBStudentStudentIdSchema),
  z.object({
    studentId: z.lazy(() => DBStudentStudentIdSchema),
    firstName: z.lazy(() => DBStudentFirstNameSchema),
    lastName: z.lazy(() => DBStudentLastNameSchema),
    note: DBTutorialBookingNoteSchema,
  })
);
export const DBTutorialCapacitySchema = z.number().int().min(1).max(9);
export const DBTutorialDescriptionSchema = z.string().max(2048);
export const DBTutorialDurationSchema = z.number().int().min(15).max(180);
export const DBTutorialHostSchema = z.lazy(() => DBStaffStaffIdSchema);
export const DBTutorialJoinLinkSchema = HttpUrlSchema;
export const DBTutorialLocationSchema = z.enum([...TUTORIAL_LOCATIONS, '']);
export const DBTutorialLinkedTutorialsSchema = z.array(DBTutorialStartDatetimeIdentifierSchema);

export const DBTutorialSchema = z.object({
  course: DBTutorialCourseSchema,
  startDatetimeIdentifier: DBTutorialStartDatetimeIdentifierSchema,
  // Optional to allow removal of attendees
  attendees: DBTutorialAttendeesSchema.optional(),
  capacity: DBTutorialCapacitySchema,
  description: DBTutorialDescriptionSchema,
  duration: DBTutorialDurationSchema,
  host: DBTutorialHostSchema,
  // Only an online tutorial has a link
  joinLink: DBTutorialJoinLinkSchema.optional(),
  location: DBTutorialLocationSchema,
  linkedTutorials: DBTutorialLinkedTutorialsSchema.optional(),
});

/**
 * Client Schemas
 */

export const TutorialIdSchema = z.string().brand<'TutorialId'>();
export const TutorialCourseIdSchema = DBTutorialCourseSchema;
export const TutorialStartDatetimeSchema = UnixTimestampSchema;
export const TutorialSuffixSchema = DBTutorialIdSuffixSchema;
export const TutorialAttendeesSchema = z.array(
  z.object({
    studentId: z.lazy(() => DBStudentStudentIdSchema),
    firstName: z.lazy(() => DBStudentFirstNameSchema),
    lastName: z.lazy(() => DBStudentLastNameSchema),
    note: TutorialBookingNoteSchema.default(''),
  })
);
export const TutorialCapacitySchema = DBTutorialCapacitySchema;
export const TutorialDescriptionSchema = DBTutorialDescriptionSchema;
export const TutorialDurationSchema = DBTutorialDurationSchema;
export const TutorialHostIdSchema = DBTutorialHostSchema;
export const TutorialJoinLinkSchema = DBTutorialJoinLinkSchema;
export const TutorialLocationSchema = DBTutorialLocationSchema;
export const TutorialLinkedTutorialIdsSchema = z.array(TutorialIdSchema);

export const TutorialSchema = z.object({
  id: TutorialIdSchema,
  courseId: TutorialCourseIdSchema.meta({ title: 'Course' }),
  startDatetime: TutorialStartDatetimeSchema,
  suffix: TutorialSuffixSchema,
  capacity: TutorialCapacitySchema.default(3).meta({ title: 'Capacity' }),
  attendees: TutorialAttendeesSchema.default([]).meta({ title: 'Attendees' }),
  description: TutorialDescriptionSchema.default(
    'Ask any questions you have about the subject!'
  ).meta({ title: 'Description' }),
  duration: TutorialDurationSchema.default(60).meta({ title: 'Duration' }),
  hostId: TutorialHostIdSchema.meta({ title: 'Host' }),
  joinLink: TutorialJoinLinkSchema.optional().meta({ title: 'Join link' }),
  location: TutorialLocationSchema.default('').meta({ title: 'Location' }),
  linkedTutorialIds: TutorialLinkedTutorialIdsSchema.optional().meta({
    title: 'Linked tutorials',
  }),
});
